import { BadgeCheck, Wrench, AlertTriangle } from "lucide-react";
import type { Product } from "@/data/productDatabase";
import { getVerification, type VerificationStatus } from "@/lib/verificationStatus";

interface VerificationBadgeProps {
  product: Product;
  /** Compact chips drop the text label on small screens. */
  size?: "sm" | "md";
  className?: string;
}

const styles: Record<VerificationStatus, string> = {
  verified: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  corrected: "bg-accent/15 text-accent border-accent/30",
  "needs-review": "bg-amber-500/15 text-amber-400 border-amber-500/30",
};

const icons: Record<VerificationStatus, typeof BadgeCheck> = {
  verified: BadgeCheck,
  corrected: Wrench,
  "needs-review": AlertTriangle,
};

const VerificationBadge = ({ product, size = "sm", className = "" }: VerificationBadgeProps) => {
  const { status, label, reason } = getVerification(product);
  const Icon = icons[status];
  const iconSize = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
  const textSize = size === "sm" ? "text-[9px]" : "text-[10px]";

  return (
    <span
      title={`${label} — ${reason}`}
      aria-label={`Data status: ${label}. ${reason}`}
      className={`inline-flex items-center gap-1 border rounded-full px-1.5 py-0.5 font-mono font-bold uppercase tracking-wider whitespace-nowrap ${styles[status]} ${textSize} ${className}`}
    >
      <Icon className={`${iconSize} shrink-0`} aria-hidden="true" />
      {label}
    </span>
  );
};

export default VerificationBadge;
